/**
 * The answer passage and Q&A for a platform page (/api/:apiId).
 *
 * These pages were a data grid with no sentence in them: an H1, a one-line
 * blurb, and a table. That is a fine reference card and a poor answer, and an
 * answer is what both a first-time reader and an AI search engine are looking
 * for. Everything below is derived from the catalog and the measured window -
 * endpoint names, USD prices, uptime, latency - so there is nothing here to
 * keep up to date by hand and nothing asserted that the page does not show.
 *
 * A sentence whose input is missing is dropped rather than softened, which is
 * the rule the figures themselves follow: no traffic renders a dash, so no
 * traffic writes no sentence.
 */
import type { BenchmarkSummary } from '../benchmarks';
import type { ApiDetail } from '../../features/catalog/model';
import { apiTotals } from '../catalog';
import { m } from '../../paraglide/messages';
import { type AnswerQuestion, list, measuredSentence, priceClause, sentences } from './answerParts';
import { openingPrice } from './apiPrice';

type ApiQuestion = AnswerQuestion;

export interface ApiAnswer {
  /** Self-contained lead paragraph, the passage an answer engine can lift. */
  passage: string;
  questions: ApiQuestion[];
}

const actionList = (detail: ApiDetail): string =>
  list(detail.endpoints.map((endpoint) => endpoint.action.toLowerCase()));

function priceSentence(detail: ApiDetail): string | undefined {
  const price = openingPrice(detail);
  if (!price) return undefined;
  return m.seo_api_price_sentence({ price: priceClause(price, detail.endpoints.length > 1) });
}

/**
 * The lead passage: what it returns, how it routes, what it costs, how it has
 * behaved. Lands around 90 to 140 words once the data is filled in, the length
 * that survives extraction as a single quotable block.
 */
function passageFor(detail: ApiDetail, summary: BenchmarkSummary | null): string {
  const { endpoints, providers } = apiTotals(detail);
  const openingInputs = { name: detail.name, count: endpoints, actions: actionList(detail) };
  const opening = endpoints === 1
    ? m.seo_api_opening_one(openingInputs)
    : m.seo_api_opening_other(openingInputs);
  const routing = providers > 1
    ? m.seo_api_routing_multi({ count: providers })
    : m.seo_api_routing_single();
  return sentences([opening, routing, priceSentence(detail), measuredSentence(m.seo_measured_subject_platform({ name: detail.name }), summary)]);
}

function questionsFor(detail: ApiDetail, summary: BenchmarkSummary | null): ApiQuestion[] {
  const questions: ApiQuestion[] = [];
  const price = openingPrice(detail);
  if (price) {
    questions.push({
      q: m.seo_q_cost({ name: detail.name }),
      a: sentences([priceSentence(detail), m.seo_wallet_answer()]),
    });
  }
  const count = detail.endpoints.length;
  questions.push({
    q: m.seo_api_q_endpoints({ name: detail.name }),
    a: count === 1
      ? m.seo_api_a_endpoints_one({ name: detail.name, actions: actionList(detail) })
      : m.seo_api_a_endpoints_other({ name: detail.name, count, actions: actionList(detail) }),
  });
  const measured = measuredSentence(m.seo_measured_subject_platform({ name: detail.name }), summary);
  if (measured) {
    questions.push({
      q: m.seo_q_reliable({ name: detail.name }),
      a: sentences([measured, m.seo_measurement_note()]),
    });
  }
  return questions;
}

/** Answer passage and Q&A for one platform page, or null with no endpoints. */
export function apiAnswer(detail: ApiDetail, summary: BenchmarkSummary | null): ApiAnswer | null {
  if (detail.endpoints.length === 0) return null;
  return { passage: passageFor(detail, summary), questions: questionsFor(detail, summary) };
}
